import React from 'react';
import { useTranslation } from 'react-i18next';
import { textParagraphs } from '../lib/publicContent';
import { ed, usePageMeta } from '../lib/editTexts';
import { PRIVACY_PROCESSING_KEYS } from '../lib/editKeys';
import PublicNav from '../components/public/PublicNav';
import PublicFooter from '../components/public/PublicFooter';
import '../styles/public.css';

const Section = ({ num, titleKey, children }) => {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 48, padding: '44px 4px', borderTop: '1px solid rgba(120,100,70,0.28)' }}>
      <div>
        <div style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 13, color: '#C0291F', marginBottom: 8 }}>{num}</div>
        <h2 {...ed(titleKey)} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 22, margin: 0, lineHeight: 1.25 }}>{t(titleKey)}</h2>
      </div>
      <div style={{ fontSize: 15, lineHeight: 1.8, color: '#5e554b' }}>{children}</div>
    </div>
  );
};

// Listen: jeder Punkt hat eine eigene Kennung (<liste>.<n>), Anzahl aus dem eingebauten Text.
const BulletList = ({ listKey }) => {
  const { t } = useTranslation();
  const items = t(listKey, { returnObjects: true });
  if (!Array.isArray(items)) return <p style={{ margin: 0 }} {...ed(listKey)}>{items}</p>;
  return (
    <ul style={{ margin: 0, paddingLeft: 20 }}>
      {items.map((item, i) => <li key={i} style={{ marginBottom: 6 }} {...ed(`${listKey}.${i}`)}>{item}</li>)}
    </ul>
  );
};

const Sub = ({ base }) => {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 {...ed(`${base}.title`)} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 17, margin: '0 0 8px', color: '#26211C' }}>{t(`${base}.title`)}</h3>
      <p {...ed(`${base}.text`)} style={{ margin: 0 }}>{t(`${base}.text`)}</p>
    </div>
  );
};

export default function PrivacyPolicyPage() {
  const { t } = useTranslation();
  usePageMeta('privacy');
  const extra = textParagraphs(t('privacy.extra.text'));
  const rights = t('privacy.rights.list', { returnObjects: true });

  return (
    <div className="kac-public">
      <div className="kac-inner">
        <PublicNav />

        {/* HERO */}
        <div className="kac-hero">
          <div className="kp-kicker" style={{ marginBottom: 12 }} {...ed('privacy.eyebrow')}>{t('privacy.eyebrow')}</div>
          <h1 {...ed('privacy.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 72, lineHeight: 0.95, margin: '0 0 24px', maxWidth: 800 }}>
            {t('privacy.title')}
          </h1>
          <p {...ed('privacy.description')} style={{ fontSize: 17, lineHeight: 1.65, color: '#5e554b', maxWidth: 600, margin: '0 0 20px' }}>
            {t('privacy.description')}
          </p>
          <div style={{ display: 'inline-block', padding: '6px 16px', borderRadius: 999, border: '1px solid rgba(120,100,70,0.3)', fontSize: 13, color: '#857A6E' }}>
            <span {...ed('privacy.last_updated')}>{t('privacy.last_updated')}</span>: <span {...ed('privacy.last_updated_date')}>{t('privacy.last_updated_date')}</span>
          </div>
        </div>

        {/* SECTIONS */}
        <Section num="01" titleKey="privacy.controller.title">
          <p {...ed('privacy.controller.name')} style={{ margin: '0 0 4px', fontWeight: 600, color: '#26211C' }}>{t('privacy.controller.name')}</p>
          <p {...ed('privacy.controller.address')} style={{ margin: '0 0 4px' }}>{t('privacy.controller.address')}</p>
          <p {...ed('privacy.controller.contact')} style={{ margin: '0 0 4px' }}>{t('privacy.controller.contact')}</p>
          <p {...ed('privacy.controller.representative')} style={{ margin: 0 }}>{t('privacy.controller.representative')}</p>
        </Section>

        <Section num="02" titleKey="privacy.data_types.title">
          <BulletList listKey="privacy.data_types.list" />
        </Section>

        <Section num="03" titleKey="privacy.legal_basis.title">
          <BulletList listKey="privacy.legal_basis.list" />
        </Section>

        <Section num="04" titleKey="privacy.rights.title">
          {Array.isArray(rights) ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 32px' }}>
              {rights.map((r, i) => <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><span style={{ color: '#C0291F', fontWeight: 700, flexShrink: 0 }}>→</span><span {...ed(`privacy.rights.list.${i}`)}>{r}</span></div>)}
            </div>
          ) : <p style={{ margin: 0 }}>{rights}</p>}
        </Section>

        <Section num="05" titleKey="privacy.processing.title">
          {PRIVACY_PROCESSING_KEYS.map(key => <Sub key={key} base={`privacy.processing.${key}`} />)}
        </Section>

        <Section num="06" titleKey="privacy.storage.title">
          <BulletList listKey="privacy.storage.list" />
        </Section>

        <Section num="07" titleKey="privacy.contact.title">
          <p {...ed('privacy.contact.dpo')} style={{ margin: '0 0 4px' }}>{t('privacy.contact.dpo')}</p>
          <p {...ed('privacy.contact.email')} style={{ margin: '0 0 4px' }}>{t('privacy.contact.email')}</p>
          <p {...ed('privacy.contact.deletion')} style={{ margin: 0 }}>{t('privacy.contact.deletion')}</p>
        </Section>

        {extra.length > 0 && (
          <Section num="+" titleKey="privacy.extra.title">
            <div {...ed('privacy.extra.text', 'paragraphs')}>
              {extra.map((p, i) => <p key={i} style={{ margin: '0 0 8px' }}>{p}</p>)}
            </div>
          </Section>
        )}

        {/* RED CTA */}
        <div className="kac-red-panel" style={{ padding: '52px 48px', margin: '48px 4px 0' }}>
          <h2 {...ed('privacy.contact_us.title')} style={{ fontFamily: "'Bricolage Grotesque',sans-serif", fontWeight: 700, fontSize: 34, margin: '0 0 16px' }}>
            {t('privacy.contact_us.title')}
          </h2>
          <p {...ed('privacy.contact_us.description')} style={{ fontSize: 17, lineHeight: 1.65, opacity: 0.88, maxWidth: 560, margin: '0 0 32px' }}>
            {t('privacy.contact_us.description')}
          </p>
          <div style={{ fontSize: 15, opacity: 0.88, marginBottom: 28 }}>
            <div style={{ marginBottom: 4 }}>
              <strong {...ed('privacy.contact_us.email.label')}>{t('privacy.contact_us.email.label')}</strong> <span {...ed('privacy.contact_us.email.address')}>{t('privacy.contact_us.email.address')}</span>
            </div>
            <div><strong {...ed('privacy.contact_us.subject.label')}>{t('privacy.contact_us.subject.label')}</strong> <span {...ed('privacy.contact_us.subject.text')}>{t('privacy.contact_us.subject.text')}</span></div>
          </div>
          <a
            href={`mailto:${t('privacy.contact_us.email.address')}?subject=${encodeURIComponent(t('privacy.contact_us.subject.text'))}`}
            style={{ display: 'inline-block', padding: '12px 26px', borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', fontWeight: 600, fontSize: 15, textDecoration: 'none' }}
          >
            <span {...ed('privacy.contact_us.button')}>{t('privacy.contact_us.button')}</span> →
          </a>
        </div>

        <PublicFooter />
      </div>
    </div>
  );
}
